// src/RmnchDashboard.js
import React from "react";
import { FaArrowUp, FaArrowDown } from "react-icons/fa";

import {
	Chart as ChartJS,
	BarElement,
	LineElement,
	PointElement,
	ArcElement,
	CategoryScale,
	LinearScale,
	Tooltip,
	Legend,
} from "chart.js";

import { Bar, Line, Doughnut } from "react-chartjs-2";
import Header from "components/Header";
// Register Chart.js components
const centerTextPlugin = {
	id: "centerText",
	afterDraw(chart) {
		// ✅ Run ONLY for doughnut charts
		if (chart.config.type !== "doughnut") return;

		const pluginOpts = chart.options.plugins?.centerText;

		// ✅ Run ONLY if explicitly enabled
		if (!pluginOpts?.enabled) return;

		const meta = chart.getDatasetMeta(0);
		if (!meta?.data?.length) return;

		const arc = meta.data[0];
		const { ctx } = chart;

		const centerX = arc.x;
		const centerY = arc.y;

		const { text, subText } = pluginOpts;

		if (!text) return;

		ctx.save();
		ctx.textAlign = "center";
		ctx.textBaseline = "middle";

		// Main text (%)
		ctx.font = "700 32px Inter, sans-serif";
		ctx.fillStyle = "#0f172a";
		ctx.fillText(text, centerX, centerY - 6);

		// Sub text
		if (subText) {
			ctx.font = "400 13px Inter, sans-serif";
			ctx.fillStyle = "#64748b";
			ctx.fillText(subText, centerX, centerY + 18);
		}

		ctx.restore();
	},
};

ChartJS.register(BarElement, CategoryScale, LinearScale, Tooltip, Legend, centerTextPlugin);


/* ---------- Example metrics ---------- */
const metrics = [
	{ key: "estimatedPregnant", title: "Estimated Pregnant Women", value: "1,00,000", series: [91000, 98500, 99000, 92500, 99800, 100000], lastMonthSeries: [19000, 25000, 25000, 23000], lastMonthLabels: ["W1", "W2", "W3", "W4"], changePct: +0.2, caption: "Projected for current period" },
	{ key: "ancRegistered", title: "Total ANC Registered", value: "95,000", series: [90000, 91000, 92000, 93000, 94000, 95000], lastMonthSeries: [20500, 24000, 21500, 24000], lastMonthLabels: ["W1", "W2", "W3", "W4"], changePct: +1.1, caption: "94% of estimated", actionLabel: "View" },
	{ key: "ancUnder15", title: "PW Registered (<15 yrs)", value: "120", series: [130, 128, 125, 123, 122, 120], lastMonthSeries: [30, 28, 31, 31], lastMonthLabels: ["W1", "W2", "W3", "W4"], changePct: -4.0, caption: "Vulnerable group monitoring" },
	{ key: "anc4plus", title: "PW received 4+ ANC", value: "65,000", series: [58000, 60000, 58000, 62000, 60000, 65000], lastMonthSeries: [11000, 15500, 12500, 17000], lastMonthLabels: ["W1", "W2", "W3", "W4"], changePct: +2.0, caption: "Coverage among ANC registered" },
	{ key: "institutionalDeliveries", title: "Institutional Deliveries", value: "88,000", series: [82000, 85500, 86000, 87000, 87500, 88000], lastMonthSeries: [20000, 21500, 20500, 22000], lastMonthLabels: ["W1", "W2", "W3", "W4"], changePct: +0.6, caption: "Includes C-sections" },
	{ key: "mmr", title: "Maternal Mortality Ratio (MMR)", value: "113", unit: "/100k LB", series: [125, 120, 118, 115, 114, 113], lastMonthSeries: [21, 25, 20, 30], lastMonthLabels: ["W1", "W2", "W3", "W4"], changePct: -1.0, caption: "Lower is better" },
];
const childMortalityTrendData = [
	{ year: "2022", nmr: 28, imr: 38 },
	{ year: "2023", nmr: 25, imr: 35 },
	{ year: "2024", nmr: 22, imr: 30 },
	{ year: "2025", nmr: 25, imr: 38 },
];
// Section 1: Antenatal Care – Coverage & Quality
const antenatalCoverage = [
	{ key: "td1", label: "PW given Td1", percent: 90 },
	{ key: "td2", label: "PW given Td2", percent: 84 },
	{ key: "bp", label: "BP Check Coverage", percent: 92 },
	{ key: "hb", label: "Hb Test Coverage", percent: 88 },
];
// Section 2: High-Risk Pregnancy & Complications
const highRiskData = {
	identified: 4200,
	managed: 3900,
	complicationReferrals: 1120,
};
const deliveryData = {
	institutional: 88000,   // includes C-sections
	homeSBA: 2000,
	cSection: 15000,
};
// Section 4: Post-Natal Care (PNC Continuum)
const pncData = {
	pnc48hrs: 83000,
	pnc7days: 80000,
	pnc6weeks: 76000,
};
// Section 5: Maternal Mortality & JSY
const maternalMortalityData = {
	mmr: 113,
	totalDeaths: 120,
	causes: {
		Hemorrhage: 35,
		Infection: 25,
		Hypertension: 15,
		Other: 45,
	},
};
// Section 6: Child Health & Survival
const childHealthData = {
	births: {
		male: 45000,
		female: 43000,
		still: 1200,
	},
	deaths: {
		male: 4500,
		female: 4100,
		still: 1200,
	},

	nutrition: {
		lbw25: 8200,
		lbw18: 2100,
		breastfeeding1hrPct: 72,
		exclusiveBF6mPct: 64,
		hbncPct: 78,
	},

	mortality: {
		neonatalDeaths: 1800,
		nmr: 25,
		infantDeaths: 2600,
		imr: 35,
		childDeaths: 3200,
		u5mr: 41,
		trend: [
			{ year: 2022, nmr: 23, imr: 38, u5mr: 41 },
			{ year: 2023, nmr: 22, imr: 36, u5mr: 43 },
			{ year: 2024, nmr: 20, imr: 32, u5mr: 41 },
			{ year: 2025, nmr: 28, imr: 39, u5mr: 45 },
		],
	},

	sickChildReferrals: 5200,
};

const jsyData = {
	beneficiaries: 72000,
	timelyPaymentPct: 92,
};
// Section 7: Immunization
const immunizationData = {
	coverage: {
		age9to11: { total: 85, male: 86, female: 84 },
		age12to23: { total: 92, male: 93, female: 91 },
	},

	antigens: {
		birthDose: 89,
		measles1: 91,
		measles2: 88,
	},

	dropout: {
		bcgToMeasles1: 15,
		penta1To3: 9,
	},

	sessions: {
		planned: 300,
		held: 270,
	},
};
// Section 8: Family Planning
const familyPlanningData = {
	eligibleCouples: 520000,
	unmetNeed: 20, // %
	postpartumFP: 14000,

	cpr: {
		target: 70,
		trend: [
			{ year: 2021, value: 58 },
			{ year: 2022, value: 61 },
			{ year: 2023, value: 63 },
			{ year: 2024, value: 60 },
			{ year: 2025, value: 69 },
		],
	},

	methods: {
		femaleSterilization: 15000,
		maleSterilization: 500,
		iud: 8000,
		ocp: 12000,
		condom: 18000,
		injectable: 6000,
	},
};
// Section 9: Adolescent Health
const adolescentHealthData = {
	girls: {
		expected: 50000,
		registered: 38000,
		clinical: 25000,
		counselling: 30000,
	},
	boys: {
		expected: 55000,
		registered: 35000,
		clinical: 18000,
		counselling: 22000,
	},
};
const DateRangeLine = () => {
	const today = new Date();
	const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);

	const formatDate = (date) =>
		date.toLocaleDateString("en-IN", {
			day: "2-digit",
			month: "short",
			year: "numeric",
		});

	return (
		<span className="text-sm text-slate-500 font-medium truncate px-3 py-1">
			State : <span className='fw-bold text-slate-700 pr-3'>Maharashtra</span>
			Date Range: <span className='fw-bold text-slate-700'>
				{formatDate(firstDay)} to {formatDate(today)}
			</span>
		</span>
	);
};
const ancFirstTrimesterData = {
	totalRegistered: 95000,
	firstTrimester: 72000,
};
const infantDeathData = {
	total: 2600,
	male: 1400,
	female: 1200,
};
function InfantDeathsPanel({ data }) {
	const chartData = {
		labels: ["Male", "Female"],
		datasets: [
			{
				data: [data.male, data.female],
				backgroundColor: ["#3B82F6", "#EC4899"],
				borderWidth: 0,
			},
		],
	};

	const options = {
		cutout: "70%",
		plugins: {
			legend: { position: "bottom" },
			tooltip: {
				callbacks: {
					label: (ctx) =>
						`${ctx.label}: ${ctx.raw.toLocaleString("en-IN")}`,
				},
			},
			datalabels: false,
			centerText: {
				enabled: true,
				text: data.total.toLocaleString("en-IN"),
				subText: "Infant Deaths",
			},
		},
	};

	return (
		<section className="bg-white rounded-2xl shadow-md p-4 h-full">
			<h3 className="text-lg font-semibold text-slate-800 mb-2">
				Total Infant Deaths
			</h3>
			<p className="text-sm text-gray-500 mb-3">
				Deaths below 1 year of age
			</p>

			<div className="w-64 h-64 mx-auto">
				<Doughnut data={chartData} options={options} />
			</div>
		</section>
	);
}

function ANCFirstTrimesterPanel({ data }) {
	const lateReg =
		data.totalRegistered - data.firstTrimester;

	const pct = Math.round(
		(data.firstTrimester / data.totalRegistered) * 100
	);

	const chartData = {
		labels: ["1st Trimester (≤12 weeks)", "Registered After 12 Weeks"],
		datasets: [
			{
				data: [data.firstTrimester, lateReg],
				backgroundColor: ["#22C55E", "#F59E0B"],
				borderWidth: 0,
			},
		],
	};

	const options = {
		cutout: "70%",
		plugins: {
			legend: { position: "bottom" },
			tooltip: {
				callbacks: {
					label: (ctx) =>
						`${ctx.label}: ${ctx.raw.toLocaleString("en-IN")}`,
				},
			},
			datalabels: false,
			centerText: {
				enabled: true,
				text: `${pct}%`,
				subText: "1st Trimester",
			},
		},
	};

	return (
		<section className="bg-white rounded-2xl shadow-md p-4 h-full">
			<h3 className="text-lg font-semibold text-slate-800 mb-2">
				ANC Registration – 1st Trimester
			</h3>
			<p className="text-sm text-gray-500 mb-3">
				Registration within 12 weeks of pregnancy
			</p>

			<div className="w-64 h-64 mx-auto">
				<Doughnut data={chartData} options={options} />
			</div>

			<div className="mt-2 text-center text-sm text-gray-600">
				{data.firstTrimester.toLocaleString("en-IN")} of{" "}
				{data.totalRegistered.toLocaleString("en-IN")} ANC registered
			</div>
		</section>
	);
}

function AdolescentGenderBarPanel({ data }) {
	const chartData = {
		labels: [
			"Expected",
			"Registered at AFHC",
			"Clinical Services",
			"Counselling",
		],
		datasets: [
			{
				label: "Adolescent Girls",
				data: [
					data.girls.expected,
					data.girls.registered,
					data.girls.clinical,
					data.girls.counselling,
				],
				backgroundColor: "#EC4899", // pink
				borderRadius: 6,
				maxBarThickness: 32,
			},
			{
				label: "Adolescent Boys",
				data: [
					data.boys.expected,
					data.boys.registered,
					data.boys.clinical,
					data.boys.counselling,
				],
				backgroundColor: "#3B82F6", // blue
				borderRadius: 6,
				maxBarThickness: 32,
			},
		],
	};

	const options = {
		responsive: true,
		maintainAspectRatio: false,
		plugins: {
			legend: {
				position: "bottom",
				labels: {
					boxWidth: 14,
					padding: 16,
				},
			},
			tooltip: {
				callbacks: {
					label: (ctx) =>
						`${ctx.dataset.label}: ${ctx.raw.toLocaleString('en-IN')}`,
				},
			},
			datalabels: false
		},
		scales: {
			x: {
				grid: { display: false },
			},
			y: {
				grid: { color: "rgba(0,0,0,0.05)" },
				ticks: {
					callback: (v) => v.toLocaleString('en-IN'),
				},
			},
		},
	};

	return (
		<section className="bg-white rounded-2xl shadow-md p-4 h-full">
			<h3 className="text-lg font-semibold text-slate-800 mb-2">
				Adolescent Coverage by Gender
			</h3>
			<p className="text-sm text-gray-500 mb-4">
				Expected vs Registered vs Services received
			</p>

			<div className="h-72">
				<Bar data={chartData} options={options} />
			</div>
		</section>
	);
}
function CPRTrendPanel({ data }) {
	const chartData = {
		labels: data.trend.map(d => d.year),
		datasets: [
			{
				label: "Actual CPR",
				data: data.trend.map(d => d.value),
				borderColor: "#3B82F6",
				backgroundColor: "rgba(59,130,246,0.1)",
				tension: 0.4,
				pointRadius: 4,
				pointBackgroundColor: "#3B82F6",
			},
			{
				label: "Target CPR",
				data: data.trend.map(() => data.target),
				borderColor: "#EF4444",
				borderDash: [6, 6],
				pointRadius: 0,
			},
		],
	};

	const options = {
		responsive: true,
		maintainAspectRatio: false,
		plugins: {
			legend: { position: "bottom" },
			tooltip: {
				callbacks: {
					label: ctx => `${ctx.dataset.label}: ${ctx.raw}%`,
				},
			},
			datalabels: false
		},
		scales: {
			y: {
				min: 0,
				max: 100,
				ticks: {
					callback: v => `${v}%`,
				},
			},
		},
	};

	return (
		<section className="bg-white rounded-2xl shadow-md p-4 h-full">
			<h3 className="text-lg font-semibold text-slate-800 mb-3">
				Modern Contraceptive Prevalence Rate (CPR)
			</h3>
			<p className="text-sm text-gray-500 mb-3">
				Trend compared with state target
			</p>

			<div className="h-64">
				<Line data={chartData} options={options} />
			</div>
		</section>
	);
}
function FPMethodMixBarPanel({ data }) {
	const labels = [
		"Female Sterilization",
		"Male Sterilization",
		"IUD",
		"OCP",
		"Condom",
		"Injectable (Antara)",
	];

	const values = [
		data.femaleSterilization,
		data.maleSterilization,
		data.iud,
		data.ocp,
		data.condom,
		data.injectable,
	];

	const chartData = {
		labels,
		datasets: [
			{
				data: values,
				backgroundColor: [
					"#6366F1",
					"#22C55E",
					"#0EA5E9",
					"#F59E0B",
					"#10B981",
					"#EC4899",
				],
				borderRadius: 6,
				maxBarThickness: 18,
			},
		],
	};

	const options = {
		indexAxis: "y",
		responsive: true,
		maintainAspectRatio: false,
		plugins: {
			legend: { display: false },
			tooltip: {
				callbacks: {
					label: ctx => ctx.raw.toLocaleString('en-IN'),
				},
			},
			datalabels: false
		},
		scales: {
			x: { grid: { color: "rgba(0,0,0,0.05)" } },
			y: { grid: { display: false } },
		},
	};

	return (
		<section className="bg-white rounded-2xl shadow-md p-4">
			<h3 className="text-lg font-semibold text-slate-800 mb-3">
				Family Planning Method Mix
			</h3>

			<div className="h-72">
				<Bar data={chartData} options={options} />
			</div>
		</section>
	);
}

function AntigenCoveragePanel({ data }) {
	const items = [
		{ label: "Birth Dose (BCG, OPV0, HepB0)", value: data.birthDose },
		{ label: "Measles-1 Coverage", value: data.measles1 },
		{ label: "Measles-2 Coverage", value: data.measles2 },
	];

	return (
		<section className="bg-white rounded-2xl shadow-md p-4">
			<h3 className="text-lg font-semibold text-slate-800 mb-4">
				Antigen-wise Coverage
			</h3>

			<div className="space-y-4">
				{items.map(i => (
					<div key={i.label}>
						<div className="flex justify-between text-sm mb-1">
							<span>{i.label}</span>
							<span className="font-semibold">{i.value}%</span>
						</div>
						<div className="h-3 bg-gray-100 rounded-full overflow-hidden">
							<div
								className="h-3 bg-gradient-to-r from-emerald-500 to-green-400"
								style={{ width: `${i.value}%` }}
							/>
						</div>
					</div>
				))}
			</div>
		</section>
	);
}
function ImmunizationDropoutPanel({ data }) {
	return (
		<section className="bg-white rounded-2xl shadow-md p-4">
			<h3 className="text-lg font-semibold text-slate-800 mb-4">
				Immunization Drop-outs
			</h3>

			<div className="space-y-4">
				<div>
					<div className="flex justify-between mb-1 text-sm">
						<span>BCG → Measles-1</span>
						<span className="font-semibold text-rose-600">
							{data.bcgToMeasles1}%
						</span>
					</div>
					<div className="h-2 bg-gray-100 rounded-full overflow-hidden">
						<div
							className="h-2 bg-rose-500"
							style={{ width: `${data.bcgToMeasles1}%` }}
						/>
					</div>
				</div>

				<div>
					<div className="flex justify-between mb-1 text-sm">
						<span>Penta-1 → Penta-3</span>
						<span className="font-semibold text-amber-600">
							{data.penta1To3}%
						</span>
					</div>
					<div className="h-2 bg-gray-100 rounded-full overflow-hidden">
						<div
							className="h-2 bg-amber-500"
							style={{ width: `${data.penta1To3}%` }}
						/>
					</div>
				</div>
			</div>
		</section>
	);
}
function ImmunizationSessionsPanel({ data }) {
	const heldPct = Math.round((data.held / data.planned) * 100);

	return (
		<section className="bg-white rounded-2xl shadow-md p-4">
			<h3 className="text-lg font-semibold text-slate-800 mb-3">
				Immunization Sessions
			</h3>

			<div className="text-sm text-gray-600 mb-1">
				Held {data.held} of {data.planned}
			</div>

			<div className="h-3 bg-gray-100 rounded-full overflow-hidden">
				<div
					className="h-3 bg-gradient-to-r from-indigo-500 to-sky-400"
					style={{ width: `${heldPct}%` }}
				/>
			</div>

			<div className="mt-1 text-xs text-gray-400">
				{heldPct}% sessions conducted
			</div>
		</section>
	);
}
function FPSummaryPanel({ eligible, unmet, postpartum }) {
	return (
		<section className="bg-white rounded-2xl shadow-md p-4">
			<h3 className="text-lg font-semibold text-slate-800 mb-4">
				Family Planning Summary
			</h3>

			<div className="space-y-4">
				<div>
					<div className="text-sm text-gray-500">Eligible Couples</div>
					<div className="text-2xl font-bold">
						{eligible.toLocaleString('en-IN')}
					</div>
				</div>

				<div>
					<div className="flex justify-between text-sm mb-1">
						<span>Unmet Need</span>
						<span className="font-semibold text-rose-600">{unmet}%</span>
					</div>
					<div className="h-2 bg-gray-100 rounded-full overflow-hidden">
						<div
							className="h-2 bg-rose-500"
							style={{ width: `${unmet}%` }}
						/>
					</div>
				</div>

				<div>
					<div className="text-sm text-gray-500">
						Postpartum FP Adoption
					</div>
					<div className="text-xl font-semibold">
						{postpartum.toLocaleString('en-IN')}
					</div>
				</div>
			</div>
		</section>
	);
}


function ImmunizationCoveragePanel({ data }) {
	const chartData = {
		labels: ["9–11 Months", "12–23 Months"],
		datasets: [
			{
				label: "Male",
				data: [data.age9to11.male, data.age12to23.male],
				backgroundColor: "#3B82F6",
			},
			{
				label: "Female",
				data: [data.age9to11.female, data.age12to23.female],
				backgroundColor: "#EC4899",
			},
		],
	};

	const options = {
		responsive: true,
		maintainAspectRatio: false,
		plugins: {
			legend: { position: "bottom" },
			tooltip: {
				callbacks: {
					label: (ctx) => `${ctx.dataset.label}: ${ctx.raw}%`,
				},
			},
			datalabels: false
		},
		scales: {
			y: {
				max: 100,
				ticks: { callback: v => `${v}%` },
			},
		},
	};

	return (
		<section className="bg-white rounded-2xl shadow-md p-4 h-full">
			<h3 className="text-lg font-semibold text-slate-800 mb-3">
				Full Immunization Coverage
			</h3>
			<div className="h-64">
				<Bar data={chartData} options={options} />
			</div>
		</section>
	);
}

function ChildMortalityTrendPanel({ data }) {
	const chartData = {
		labels: data.map(d => d.year),
		datasets: [
			{
				label: "NMR",
				data: data.map(d => d.nmr),
				borderColor: "#EF4444",
				tension: 0.4,
			},
			{
				label: "IMR",
				data: data.map(d => d.imr),
				borderColor: "#F59E0B",
				tension: 0.4,
			},
			{
				label: "U5MR",
				data: data.map(d => d.u5mr),
				borderColor: "#3B82F6",
				tension: 0.4,
			},
		],
	};

	const options = {
		responsive: true,
		maintainAspectRatio: false,
		plugins: { legend: { position: "bottom" }, datalabels: false },
		scales: { y: { beginAtZero: true } },
	};

	return (
		<section className="bg-white rounded-2xl shadow-md p-4 h-full">
			<h3 className="text-lg font-semibold text-slate-800 mb-3">
				Child Mortality Trends
			</h3>
			<div className="h-64">
				<Line data={chartData} options={options} />
			</div>
		</section>
	);
}

function NutritionCarePanel({ data }) {
	const items = [
		{ label: "Breastfed within 1 hour", value: data.breastfeeding1hrPct },
		{ label: "Exclusive BF up to 6 months", value: data.exclusiveBF6mPct },
		{ label: "HBNC Coverage", value: data.hbncPct },
	];

	return (
		<section className="bg-white rounded-2xl shadow-md p-4">
			<h3 className="text-lg font-semibold text-slate-800 mb-4">
				Nutrition & Early Care
			</h3>

			<div className="space-y-4">
				{items.map((i) => (
					<div key={i.label}>
						<div className="flex justify-between text-sm mb-1">
							<span>{i.label}</span>
							<span className="font-semibold">{i.value}%</span>
						</div>
						<div className="h-3 bg-gray-100 rounded-full overflow-hidden">
							<div
								className="h-3 bg-gradient-to-r from-emerald-500 to-green-400"
								style={{ width: `${i.value}%` }}
							/>
						</div>
					</div>
				))}
			</div>

			<div className="mt-2 text-sm text-gray-600">
				LBW &lt;2.5 kg: <b>{data.lbw25.toLocaleString('en-IN')}</b> <br />
				LBW &lt;1.8 kg: <b>{data.lbw18.toLocaleString('en-IN')}</b>
			</div>
		</section>
	);
}

function BirthCompositionPanel({ data }) {
	const totalBirths =
		data.male + data.female + data.still;

	const chartData = {
		labels: ["Male", "Female", "Still Birth"],
		datasets: [
			{
				data: [data.male, data.female, data.still],
				backgroundColor: ["#3B82F6", "#EC4899", "#9CA3AF"],
				borderWidth: 0,
			},
		],
	};

	const options = {
		cutout: "70%",
		plugins: {
			legend: { position: "bottom" },
			tooltip: {
				callbacks: {
					label: (ctx) => {
						const v = ctx.raw;
						const pct = Math.round((v / totalBirths) * 100);
						return `${ctx.label}: ${v.toLocaleString('en-IN')} (${pct}%)`;
					},
				},
			},
			datalabels: false,
			centerText: {
				enabled: true,
				text: totalBirths.toLocaleString('en-IN'),
				subText: "Total Births",
			},
		},
	};

	return (
		<section className="bg-white rounded-2xl shadow-md p-4">
			<h3 className="text-lg font-semibold text-slate-800 mb-3">
				Birth Composition
			</h3>
			<div className="w-64 h-64 mx-auto">
				<Doughnut data={chartData} options={options} />
			</div>
		</section>
	);
}
function DeathCompositionPanel({ data }) {
	const totalBirths =
		data.male + data.female + data.still;

	const chartData = {
		labels: ["Male", "Female"],
		datasets: [
			{
				data: [data.male, data.female],
				backgroundColor: ["#3B82F6", "#EC4899"],
				borderWidth: 0,
			},
		],
	};

	const options = {
		cutout: "70%",
		plugins: {
			legend: { position: "bottom" },
			tooltip: {
				callbacks: {
					label: (ctx) => {
						const v = ctx.raw;
						const pct = Math.round((v / totalBirths) * 100);
						return `${ctx.label}: ${v.toLocaleString('en-IN')} (${pct}%)`;
					},
				},
			},
			datalabels: false,
			centerText: {
				enabled: true,
				text: totalBirths.toLocaleString('en-IN'),
				subText: "Total Births",
			},
		},
	};

	return (
		<section className="bg-white rounded-2xl shadow-md p-4">
			<h3 className="text-lg font-semibold text-slate-800 mb-3">
				Child Deaths
			</h3>
			<div className="w-64 h-64 mx-auto">
				<Doughnut data={chartData} options={options} />
			</div>
		</section>
	);
}

function MaternalMortalityJSYPanel({ mortality, jsy }) {
	const causeLabels = Object.keys(mortality.causes);
	const causeValues = Object.values(mortality.causes);

	const chartData = {
		labels: causeLabels,
		datasets: [
			{
				data: causeValues,
				backgroundColor: [
					"#EF4444", // hemorrhage
					"#F59E0B", // infection
					"#6366F1", // hypertension
					"#9CA3AF", // other
				],
				borderWidth: 0,
			},
		],
	};

	const options = {
		cutout: "70%",
		plugins: {
			legend: {
				position: "bottom",
				labels: {
					boxWidth: 14,
					padding: 16,
					usePointStyle: true,
					font: { size: 13 },
				},
			},
			tooltip: {
				backgroundColor: "#0f172a",
				titleColor: "#fff",
				bodyColor: "#e5e7eb",
				padding: 12,
				callbacks: {
					label: (ctx) => {
						const value = ctx.raw || 0;
						const total = mortality.totalDeaths || 1;
						const pct = Math.round((value / total) * 100);
						return `${ctx.label}: ${value} (${pct}%)`;
					},
				},
			},
			datalabels: false,
			// Center text (TOTAL deaths)
			centerText: {
				enabled: true,
				text: mortality.totalDeaths.toString(),
				subText: "Maternal Deaths",
			},
		},
	};

	return (
		<section className="bg-white rounded-2xl shadow-md p-4">
			<div className="mb-4">
				<h3 className="text-lg font-semibold text-slate-800">
					Maternal Mortality & JSY Performance
				</h3>
				<p className="text-sm text-gray-500">
					Causes of maternal deaths and JSY scheme efficiency
				</p>
			</div>

			<div className="grid grid-cols-1 lg:grid-cols-2 gap-2 items-center">
				{/* Left: Maternal mortality */}
				<div>

					<div className="w-72 h-72 mx-auto">
						<Doughnut data={chartData} options={options} />
					</div>
				</div>

				{/* Right: JSY */}
				<div className="space-y-6">
					<div className="mb-3">
						<div className="text-xs uppercase text-gray-400">
							Maternal Mortality Ratio (MMR)
						</div>
						<div className="text-2xl font-bold text-slate-800">
							{mortality.mmr} <span className="text-sm font-medium text-gray-500">/100k LB</span>
						</div>
					</div>

					<div>
						<div className="text-xs uppercase text-gray-400">
							JSY Beneficiaries
						</div>
						<div className="text-2xl font-bold text-slate-800">
							{jsy.beneficiaries.toLocaleString('en-IN')}
						</div>
					</div>

					<div>
						<div className="flex items-center justify-between mb-1">
							<span className="text-sm text-gray-600">
								JSY Payment Timeliness
							</span>
							<span className="text-sm font-semibold text-slate-800">
								{jsy.timelyPaymentPct}%
							</span>
						</div>

						<div className="w-full bg-gray-100 h-3 rounded-full overflow-hidden">
							<div
								className="h-3 rounded-full bg-gradient-to-r from-emerald-500 to-green-400"
								style={{ width: `${jsy.timelyPaymentPct}%` }}
							/>
						</div>

						<div className="mt-1 text-xs text-gray-400">
							Percentage of payments completed within stipulated time
						</div>
					</div>
				</div>
			</div>
		</section>
	);
}
function DeliveryDistributionPanel({ data }) {
	const institutional = data?.institutional ?? 0;
	const homeSBA = data?.homeSBA ?? 0;
	const cSection = data?.cSection ?? 0;

	const totalDeliveries = institutional + homeSBA;

	const chartData = {
		labels: [
			"Institutional (Normal)",
			"C-Section",
			"Home Delivery (SBA)",
		],
		datasets: [
			{
				data: [
					institutional - cSection, // normal institutional
					cSection,
					homeSBA,
				],
				backgroundColor: ["#3B82F6", "#F59E0B", "#10B981"],
				hoverBackgroundColor: ["#2563EB", "#D97706", "#059669"],
				borderWidth: 0,
			},
		],
	};

	const options = {
		cutout: "70%",
		responsive: true,
		maintainAspectRatio: false,
		plugins: {
			legend: {
				position: "bottom",
				labels: {
					boxWidth: 14,
					padding: 16,
					usePointStyle: true,
					font: {
						size: 13,
						weight: "500",
					},
					color: "#334155",
				},
			},
			tooltip: {
				backgroundColor: "#0f172a",
				titleColor: "#fff",
				bodyColor: "#e5e7eb",
				padding: 12,
				callbacks: {
					label: (ctx) => {
						const value = ctx.raw || 0;
						const pct = totalDeliveries
							? Math.round((value / totalDeliveries) * 100)
							: 0;
						return `${ctx.label}: ${value.toLocaleString('en-IN')} (${pct}%)`;
					},
				},
			},
			datalabels: false,
			// 🔥 center text (TOTAL deliveries)
			centerText: {
				enabled: true,
				text: totalDeliveries.toLocaleString('en-IN'),
				subText: "Total Deliveries",
			},
			// datalabels: false,
		},
	};

	return (
		<section className="bg-white rounded-2xl shadow-md p-4">
			<div className="mb-3">
				<h3 className="text-lg font-semibold text-slate-800">
					Delivery Care & Institutionalization
				</h3>
				<p className="text-sm text-gray-500">
					Distribution of deliveries by place & type
				</p>
			</div>

			<div className="w-72 h-72 mx-auto">
				<Doughnut data={chartData} options={options} />
			</div>
		</section>
	);
}
function PNCCascadePanel({ data }) {
	const stages = [
		{ key: "pnc48hrs", label: "PNC within 48 hrs", value: data.pnc48hrs },
		{ key: "pnc7days", label: "PNC within 7 days", value: data.pnc7days },
		{ key: "pnc6weeks", label: "PNC within 6 weeks", value: data.pnc6weeks },
	];

	const maxValue = Math.max(...stages.map(s => s.value), 1);

	return (
		<section className="bg-white rounded-2xl shadow-md p-4">
			{/* Header */}
			<div className="mb-4">
				<h3 className="text-lg font-semibold text-slate-800">
					Post-Natal Care (PNC) Continuum
				</h3>
				<p className="text-sm text-gray-500">
					Follow-up of mothers after delivery
				</p>
			</div>

			<div className="space-y-5">
				{stages.map((stage, index) => {
					const widthPct = Math.round((stage.value / maxValue) * 100);

					let dropPct = null;
					if (index > 0) {
						const prev = stages[index - 1].value;
						dropPct = prev
							? Math.round(((prev - stage.value) / prev) * 100)
							: 0;
					}

					return (
						<div key={stage.key}>
							{/* Label row */}
							<div className="flex items-center justify-between mb-1">
								<span className="text-sm text-gray-600">
									{stage.label}
								</span>
								<span className="text-sm font-semibold text-slate-800">
									{stage.value.toLocaleString('en-IN')}
								</span>
							</div>

							{/* Bar */}
							<div className="relative h-3 bg-gray-100 rounded-full overflow-hidden">
								<div
									className="h-3 rounded-full bg-gradient-to-r from-indigo-500 to-sky-400"
									style={{ width: `${widthPct}%` }}
								/>
							</div>

							{/* Drop-off info */}
							{dropPct !== null && (
								<div className="mt-1 text-xs text-gray-400">
									{dropPct}% drop from previous stage
								</div>
							)}
						</div>
					);
				})}
			</div>
		</section>
	);
}
function ChildMortalityTrend({ data = [] }) {
	/**
	 * data format:
	 * [
	 *   { year: "2022", nmr: 28, imr: 38 },
	 *   { year: "2023", nmr: 25, imr: 35 },
	 *   { year: "2024", nmr: 22, imr: 30 },
	 * ]
	 */

	const labels = data.map(d => d.year);

	const chartData = {
		labels,
		datasets: [
			{
				label: "Neonatal Mortality Rate (NMR)",
				data: data.map(d => d.nmr),
				borderColor: "#6366F1", // indigo
				backgroundColor: "rgba(99,102,241,0.12)",
				tension: 0.35,
				pointRadius: 4,
				pointBackgroundColor: "#6366F1",
			},
			{
				label: "Infant Mortality Rate (IMR)",
				data: data.map(d => d.imr),
				borderColor: "#F59E0B", // amber
				backgroundColor: "rgba(245,158,11,0.12)",
				tension: 0.35,
				pointRadius: 4,
				pointBackgroundColor: "#F59E0B",
			},
		],
	};

	const options = {
		responsive: true,
		maintainAspectRatio: false,
		plugins: {
			legend: {
				position: "bottom",
				labels: {
					boxWidth: 10,
					usePointStyle: true,
				},
			},
			tooltip: {
				backgroundColor: "#0f172a",
				titleColor: "#fff",
				bodyColor: "#e5e7eb",
				padding: 10,
				displayColors: true,
			},
			datalabels: false
		},
		scales: {
			x: {
				grid: { display: false },
				ticks: { color: "#64748b" },
			},
			y: {
				grid: { color: "rgba(0,0,0,0.05)" },
				ticks: { color: "#64748b" },
			},
		},
	};

	return (
		<section className="bg-white rounded-2xl shadow p-4 h-full">
			<div className="mb-3">
				<h3 className="text-lg font-semibold text-slate-800">
					Child Mortality Trends
				</h3>
				<p className="text-sm text-gray-500">
					NMR & IMR comparison (2022 – 2024)
				</p>
			</div>

			<div className="h-[280px]">
				<Line data={chartData} options={options} />
			</div>
		</section>
	);
}
/* ---------- Reusable CompactKpiCard (Chart.js mini bar) ---------- */
function CompactKpiCard({
	title,
	value,
	unit = "",
	series = [],
	lastMonthSeries = [],
	lastMonthLabels = [],
	changePct = null,
	caption = "",
	actionLabel = null,
	onActionClick = null,
}) {
	const lastVal = Array.isArray(series) && series.length ? series[series.length - 1] : null;
	const prevVal = Array.isArray(series) && series.length > 1 ? series[series.length - 2] : null;

	const computedPct =
		typeof changePct === "number"
			? changePct
			: prevVal != null && lastVal != null
				? ((lastVal - prevVal) / Math.max(1, Math.abs(prevVal))) * 100
				: null;

	const showPct = typeof computedPct === "number";
	const pctUp = showPct ? computedPct > 0 : null;
	const absPct = showPct ? Math.abs(Number(computedPct).toFixed(1)) : null;

	const bars = lastMonthSeries && lastMonthSeries.length ? lastMonthSeries : series.slice(-4);
	const labels = lastMonthLabels && lastMonthLabels.length ? lastMonthLabels : bars.map((_, i) => `${i + 1}`);

	const barData = {
		labels,
		datasets: [
			{
				data: bars,
				backgroundColor: "#60A5FA",
				borderRadius: 6,
				borderSkipped: false,
				maxBarThickness: 15,
			},
		],
	};

	const barOptions = {
		responsive: true,
		maintainAspectRatio: false,
		animation: false,
		plugins: {
			legend: { display: false },
			tooltip: {
				enabled: true,
				backgroundColor: "#0f172a",
				titleColor: "#fff",
				bodyColor: "#e6eef8",
				padding: 8,
				displayColors: false,
				callbacks: {
					title: (tt) => (tt && tt.length ? String(tt[0].label) : ""),
					label: (tt) => {
						const val = tt.raw;
						return typeof val === "number" ? val.toLocaleString('en-IN') : String(val);
					},
				},
			},
			datalabels: false,
		},
		scales: { x: { grid: { display: false }, ticks: { display: false } }, y: { grid: { display: false }, ticks: { display: false } } },
	};

	return (
		<div className="bg-white rounded-2xl shadow-md p-3">
			<div className="flex items-start justify-between">
				<div><div className="text-sm font-medium text-slate-700">{title}</div></div>
				{/* {actionLabel ? <button onClick={onActionClick} className="text-xs text-indigo-500">{actionLabel} →</button> : null} */}
			</div>

			<div className="mt-3 flex items-center justify-between gap-2">
				<div>
					<div className="text-3xl font-extrabold text-slate-900">{value}{unit ? <span className="text-sm text-gray-500 ml-1">{unit}</span> : null}</div>
					{showPct ? <div className={`inline-flex items-center px-2 py-1 mt-2 rounded-full text-sm font-medium ${pctUp ? "bg-emerald-50 text-emerald-700" : "bg-rose-50 text-rose-700"}`}>{pctUp ? <FaArrowUp className="mr-1" /> : <FaArrowDown className="mr-1" />}{absPct}%</div> : null}
				</div>

				<div className="w-[120px] h-[56px]">
					<Bar data={barData} options={barOptions} />
				</div>
			</div>

			{caption ? <div className="mt-3 text-xs text-gray-400">{caption}</div> : null}
		</div>
	);
}
// function ANCFunnel({ data = {}, onStageClick = () => { } }) {
// 	// data: { estimatedPregnant, ancRegistered, ancRegisteredFirstTrimester, anc4plus }
// 	const {
// 		estimatedPregnant = 100000,
// 		ancRegistered = 95000,
// 		ancRegisteredFirstTrimester = 75000,
// 		anc4plus = 65000,
// 	} = data;

// 	// compute shares relative to estimated pregnant
// 	const toPct = (v, base) => (base ? Math.round((v / base) * 1000) / 10 : 0);
// 	const stages = [
// 		{ key: "estimated", label: "Estimated Pregnant", value: estimatedPregnant, color: "from-indigo-500 to-indigo-400" },
// 		{ key: "ancReg", label: "ANC Registered", value: ancRegistered, color: "from-sky-500 to-blue-400" },
// 		{ key: "firstTrim", label: "ANC in 1st Trimester", value: ancRegisteredFirstTrimester, color: "from-emerald-500 to-green-400" },
// 		{ key: "anc4", label: "Received 4+ ANC", value: anc4plus, color: "from-yellow-400 to-amber-400" },
// 	];

// 	const max = Math.max(...stages.map(s => s.value), 1);

// 	return (
// 		<section className="mt-2 bg-white rounded-2xl shadow p-4">
// 			<div className="flex items-center justify-between mb-4">
// 				<h3 className="text-lg font-semibold text-slate-800">ANC </h3>
// 				<div className="text-sm text-gray-500">Conversion from estimated pregnancies → 4+ ANC</div>
// 			</div>

// 			<div className="grid grid-cols-1 gap-3">
// 				{stages.map((s, idx) => {
// 					const widthPct = Math.round((s.value / max) * 100);
// 					const convPct = toPct(s.value, estimatedPregnant);
// 					const nextStage = stages[idx + 1];
// 					const dropOff = nextStage ? Math.round(((s.value - nextStage.value) / Math.max(1, s.value)) * 100) : 0;

// 					return (
// 						<div key={s.key} className="flex items-center gap-2">
// 							<div className="w-40">
// 								<div className="text-xs text-gray-500">{s.label}</div>
// 								<div className="text-sm font-semibold text-slate-800">{s.value.toLocaleString('en-IN')}</div>
// 							</div>

// 							<div className="flex-1">
// 								<div className="relative h-8 bg-gray-100 rounded overflow-hidden">
// 									<div
// 										role="button"
// 										tabIndex={0}
// 										onClick={() => onStageClick(s.key)}
// 										onKeyDown={(e) => e.key === "Enter" && onStageClick(s.key)}
// 										className={`absolute left-0 top-0 h-full rounded ${s.color}`}
// 										style={{ width: `${widthPct}%`, background: undefined }}
// 									>
// 										{/* we use inline gradient via Tailwind classes above, but since we set style width,
// 						  Tailwind's gradient needs manual style. We'll fallback to solid color for reliability. */}
// 										<div style={{ width: "100%", height: "100%", background: "linear-gradient(90deg, rgba(79,70,229,0.95), rgba(59,130,246,0.95))" }}></div>
// 									</div>
// 								</div>

// 								<div className="mt-2 flex items-center justify-between text-xs text-gray-500">
// 									<div>{convPct}% of estimated</div>
// 									{nextStage ? <div>{dropOff}% drop-off → {nextStage.label}</div> : <div />}
// 								</div>
// 							</div>
// 						</div>
// 					);
// 				})}
// 			</div>
// 		</section>
// 	);
// }
function AntenatalCoveragePanel({ data = [] }) {
	const getColor = (pct) => {
		if (pct >= 90) return "bg-emerald-500";
		if (pct >= 75) return "bg-amber-400";
		return "bg-red-500";
	};

	return (
		<section className="bg-white rounded-2xl shadow-md p-4 h-full">
			<div className="mb-4">
				<h3 className="text-lg font-semibold text-slate-800">
					Antenatal Care – Coverage & Quality
				</h3>
				<p className="text-sm text-gray-500">
					Essential services coverage among registered PW
				</p>
			</div>

			<div className="space-y-4">
				{data.map((item) => (
					<div key={item.key}>
						<div className="flex items-center justify-between mb-1">
							<span className="text-sm text-gray-600">{item.label}</span>
							<span className="text-sm font-semibold text-slate-800">
								{item.percent}%
							</span>
						</div>

						<div className="w-full bg-gray-100 h-2.5 rounded">
							<div
								className={`h-2.5 rounded ${getColor(item.percent)}`}
								style={{ width: `${item.percent}%` }}
							/>
						</div>
					</div>
				))}
			</div>
		</section>
	);
}
function HighRiskPregnancyPanel({ data }) {
	const identified = data?.identified ?? 0;
	const managed = data?.managed ?? 0;
	const notManaged = Math.max(identified - managed, 0);
	const managedPct = identified
		? Math.round((managed / identified) * 100)
		: 0;

	const chartData = {
		labels: ["Managed", "Not Managed"],
		datasets: [
			{
				data: [managed, notManaged],
				backgroundColor: ["#22C55E", "#FBBF24"], // emerald, amber
				hoverBackgroundColor: ["#16A34A", "#F59E0B"],
				borderWidth: 0,
			},
		],
	};

	const options = {
		cutout: "75%",
		plugins: {
			legend: {
				position: "bottom",
			},
			tooltip: {
				callbacks: {
					label: (ctx) => {
						const value = ctx.raw || 0;
						const total = identified || 1;
						const pct = Math.round((value / total) * 100);
						return `${ctx.label}: ${value} (${pct}%)`;
					},
				},
			},
			datalabels: false,
			// 🔥 Explicitly enable center text ONLY HERE
			centerText: {
				enabled: true,
				text: `${managedPct}%`,
				subText: "Managed",
			},
		},
	};
	return (
		<section className="bg-white rounded-2xl shadow-md p-4 h-100">
			{/* Header */}
			<div className="mb-4">
				<h3 className="text-lg font-semibold text-slate-800">
					High-Risk Pregnancy Management
				</h3>
				<p className="text-sm text-gray-500">
					Identification vs management effectiveness
				</p>
			</div>

			<div className="grid grid-cols-1 md:grid-cols-2 gap-2 items-center">
				{/* KPI column */}
				<div className="space-y-5">
					<div>
						<div className="text-xs uppercase text-gray-400">
							Identified
						</div>
						<div className="text-2xl font-bold text-slate-800">
							{identified.toLocaleString('en-IN')}
						</div>
					</div>

					<div>
						<div className="text-xs uppercase text-gray-400">
							Managed
						</div>
						<div className="text-2xl font-bold text-slate-800">
							{managed.toLocaleString('en-IN')}
						</div>
					</div>

					<div className="pt-3 border-t">
						<div className="text-xs uppercase text-gray-400">
							Complication Referrals
						</div>
						<div className="text-xl font-semibold text-slate-700">
							{data.complicationReferrals.toLocaleString('en-IN')}
						</div>
					</div>
				</div>

				{/* Donut column */}
				<div className="flex justify-center">
					<div className="w-64 h-64 mx-auto">
						<Doughnut data={chartData} options={options} />
					</div>
				</div>
			</div>
		</section>
	);
}

/* ---------- Main page component ---------- */
export default function RmnchDashboard() {
	const handleMenuClick = () => console.log("menu clicked (header)");

	return (
		<div className="min-h-screen bg-gray-50">
			<Header onMenuClick={handleMenuClick} dashboardName="RMNCH Dashboard" />

			<div className=" mx-auto p-3">
				<div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-2">
					{metrics.map((m) => (
						<CompactKpiCard key={m.key} {...m} />
					))}
				</div>
				{/* <ANCFunnel
							data={{
								estimatedPregnant: Number(
									metrics.find(m => m.key === "estimatedPregnant")?.series?.slice(-1)[0] || 100000
								),
								ancRegistered: Number(
									metrics.find(m => m.key === "ancRegistered")?.series?.slice(-1)[0] || 95000
								),
								ancRegisteredFirstTrimester: 75000,
								anc4plus: Number(
									metrics.find(m => m.key === "anc4plus")?.series?.slice(-1)[0] || 65000
								),
							}}
							onStageClick={(stageKey) => console.log("drill into", stageKey)}
						/> */}
				<div className="grid grid-cols-1 md:grid-cols-4 gap-2 mt-2">

					{/* Column 1 – ANC 1st Trimester */}
					<div>
						<ANCFirstTrimesterPanel data={ancFirstTrimesterData} />
					</div>

					{/* Column 2 – Infant Deaths */}
					<div>
						<InfantDeathsPanel data={infantDeathData} />
					</div>

					{/* Column 3 – Antenatal Coverage */}
					<div>
						<AntenatalCoveragePanel data={antenatalCoverage} />
					</div>

					{/* Column 4 – High Risk Pregnancy */}
					<div>
						<HighRiskPregnancyPanel data={highRiskData} />
					</div>

				</div>

				<div className="grid grid-cols-1 lg:grid-cols-2 gap-2 mt-3">

					{/* LEFT 50% */}
					<div className="grid grid-cols-1 md:grid-cols-2 gap-2">
						<DeliveryDistributionPanel data={deliveryData} />
						<PNCCascadePanel data={pncData} />
					</div>

					{/* RIGHT 50% */}
					<div>
						<MaternalMortalityJSYPanel
							mortality={maternalMortalityData}
							jsy={jsyData}
						/>
					</div>

				</div>
				<div className="grid grid-cols-1 lg:grid-cols-4 gap-2 mt-3">

					{/* LEFT 50% */}
					{/* <div className="grid grid-cols-1 md:grid-cols-2 gap-2"> */}
					<BirthCompositionPanel data={childHealthData.births} />
					<DeathCompositionPanel data={childHealthData.deaths} />
					<NutritionCarePanel data={childHealthData.nutrition} />
					<ChildMortalityTrendPanel data={childHealthData.mortality.trend} />
					{/* </div> */}

					{/* RIGHT 50% */}
					{/* <div>
						
					</div> */}

				</div>

				{/* Section 6 – Child Health & Survival */}
				<div className="grid grid-cols-1 lg:grid-cols-4 gap-2 mt-2">




					{/* <div className="bg-white rounded-2xl shadow-md p-4">
						<h3 className="text-lg font-semibold mb-2">
							Sick Child Referrals
						</h3>
						<div className="text-3xl font-bold text-slate-800">
							{childHealthData.sickChildReferrals.toLocaleString('en-IN')}
						</div>
					</div> */}
				</div>
				{/* Section 7 – Immunization */}
				<div className="grid grid-cols-1 lg:grid-cols-5 gap-2 mt-2">

					{/* LEFT: 40% */}
					<div className="lg:col-span-2">
						<ImmunizationCoveragePanel data={immunizationData.coverage} />
					</div>

					{/* RIGHT: 60% */}
					<div className="lg:col-span-3 grid grid-cols-1 md:grid-cols-2 gap-2">
						<AntigenCoveragePanel data={immunizationData.antigens} />
						<ImmunizationDropoutPanel data={immunizationData.dropout} />
						<ImmunizationSessionsPanel data={immunizationData.sessions} />
						<div className="bg-white rounded-2xl shadow-md p-4">
							<div className="flex items-center justify-between mb-3">
								<h3 className="text-lg font-semibold text-slate-800">
									Sick Child Referrals
								</h3>
								<span className="text-xs px-2 py-1 rounded-full bg-rose-50 text-rose-600 font-medium">
									Needs follow-up
								</span>
							</div>

							<div className="text-4xl font-extrabold text-slate-900 mb-2">
								{childHealthData.sickChildReferrals.toLocaleString('en-IN')}
							</div>

							<div className="text-sm text-gray-500 mb-2">
								Children referred for higher-level care
							</div>

							{/* Visual severity bar */}
							<div className="h-2 bg-gray-100 rounded-full overflow-hidden">
								<div
									className="h-2 bg-gradient-to-r from-rose-500 to-orange-400"
									style={{ width: "70%" }} // optional contextual indicator
								/>
							</div>
						</div>

					</div>

				</div>
				{/* Section 8 – Family Planning */}
				<div className="grid grid-cols-1 lg:grid-cols-2 gap-2 mt-2">
					<CPRTrendPanel data={familyPlanningData.cpr} />
					<FPMethodMixBarPanel data={familyPlanningData.methods} />


				</div>
				{/* Section 9 – Adolescent Health */}
				{/* Section 9 – Adolescent Health */}
				<div className="mt-2 grid grid-cols-1 lg:grid-cols-10 gap-2">

					{/* 50% */}
					<div className="lg:col-span-5">
						<AdolescentGenderBarPanel data={adolescentHealthData} />
					</div>

					{/* 30% */}
					<div className="lg:col-span-3">
						<FPSummaryPanel
							eligible={familyPlanningData.eligibleCouples}
							unmet={familyPlanningData.unmetNeed}
							postpartum={familyPlanningData.postpartumFP}
						/>
					</div>

					{/* 20% spacer (optional future KPIs) */}
					<div className="hidden lg:block lg:col-span-2" />

				</div>







			</div>


		</div>
	);
}
